import type { PrintoutSettings } from "./Header/usePrintout";

/**
 * Printout settings as the user-settings API spells them (`printoutSortBy`, `printoutColumns`, …) — the
 * body Printout 1's autosave and "Save as default" send, and the settings a printout event logs.
 */
export const toPrintoutSettingsPayload = (printoutSettings: PrintoutSettings) => ({
	printGaps: printoutSettings.printGaps,
	showRoleGroupStats: printoutSettings.showRoleGroupStats,
	dailyPrintoutGrouping: printoutSettings.dailyPrintoutGrouping,
	divideGroupsByPage: printoutSettings.divideGroupsByPage,
	separateSheetsBy: printoutSettings.separateSheetsBy,
	customSheetTabs: printoutSettings.customSheetTabs,
	showAdmins: printoutSettings.showAdmins,
	showLocationCensus: printoutSettings.showLocationCensus,
	showPtoSheet: printoutSettings.showPtoSheet,
	showCallOutsSheet: printoutSettings.showCallOutsSheet,
	includeTimeOffReason: printoutSettings.includeTimeOffReason,
	printoutSortBy: printoutSettings.sortBy,
	printoutColumns: printoutSettings.columns,
	staffPerRow: printoutSettings.staffPerRow,
	rowGroupingBy: printoutSettings.rowGroupingBy,
	columnGroupingBy: printoutSettings.columnGroupingBy,
	spaceBetweenGroups: printoutSettings.spaceBetweenGroups,
	printoutOrientation: printoutSettings.orientation,
	printoutFitToPage: printoutSettings.fitToPage,
	printoutFitToWidth: printoutSettings.fitToWidth,
	printoutFitToHeight: printoutSettings.fitToHeight,
	hideHppdNursingHours: printoutSettings.hideHppdNursingHours,
	printoutColumnWidths: printoutSettings.printoutColumnWidths,
	flagNotOnboarded: printoutSettings.flagNotOnboarded,
	flagAgency: printoutSettings.flagAgency,
	mergeSimilarShiftTimes: printoutSettings.mergeSimilarShiftTimes,
	alignGroupTimesToPopular: printoutSettings.alignGroupTimesToPopular,
	customTimeGroups: printoutSettings.customTimeGroups,
	appendRoleToName: printoutSettings.appendRoleToName,
	hideColumnNames: printoutSettings.hideColumnNames,
	appendEmptyColumns: printoutSettings.appendEmptyColumns,
	customColumnNames: printoutSettings.customColumnNames,
	showDhppd: printoutSettings.showDhppd,
	showNhppd: printoutSettings.showNhppd,
	showActualStaffColumn: printoutSettings.showActualStaffColumn,
	showActualHoursColumn: printoutSettings.showActualHoursColumn,
	hppdIncludeTrainees: printoutSettings.hppdIncludeTrainees,
});

/**
 * The user_settings columns that "Use department printouts" resets to the department's: every column the
 * popup's settings payload above writes. That is the 32 Detailed-printout preset columns, the Summary
 * printout's 5 options, and the 2 legacy mirrors of the grouping list (UserSettingsProvider writes the
 * first two grouping entries there too) — 37 payload keys, 39 columns.
 *
 * A plain string-literal array on purpose: the backend's drift test reads it from this file and pins it
 * against its own PRINTOUT_POPUP_COLUMNS, and `printoutPopupRestoreFields.spec.tsx` pins it against the
 * payload through the provider's request mapping.
 */
export const PRINTOUT_POPUP_RESTORE_FIELDS: readonly string[] = [
	"print_gaps",
	"show_role_group_stats",
	"daily_printout_grouping_list",
	"divide_groups_by_page",
	"separate_sheets_by",
	"show_pto_sheet",
	"show_call_outs_sheet",
	"printout_include_time_off_reason",
	"printout_columns",
	"printout_sort_by",
	"staff_per_row",
	"row_grouping_by",
	"column_grouping_by",
	"printout_space_between_groups",
	"printout_orientation",
	"printout_fit_to_page",
	"printout_fit_to_width",
	"printout_fit_to_height",
	"printout_hide_hppd_nursing_hours",
	"printout_flag_not_onboarded",
	"printout_flag_agency",
	"printout_merge_similar_shift_times",
	"printout_align_group_times_to_popular",
	"printout_custom_time_groups",
	"printout_append_role_to_name",
	"printout_hide_column_names",
	"printout_append_empty_columns",
	"printout_custom_column_names",
	"custom_sheet_tabs",
	"show_admins",
	"show_location_census",
	"printout_column_widths",
	"printout_show_dhppd",
	"printout_show_nhppd",
	"printout_show_actual_staff_column",
	"printout_show_actual_hours_column",
	"printout_hppd_include_trainees",
	"daily_printout_grouping",
	"secondary_daily_printout_grouping",
];
